import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  LogOut, RefreshCw, Search, Filter, Edit2, Trash2, 
  Eye, X, Check, Clock, DollarSign, FileText, 
  Phone, Mail, MapPin, Calendar, ChevronDown
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LOGO_URL = "https://customer-assets.emergentagent.com/job_tilefix-scheduler/artifacts/c05hbcds_IMG_1809.png";

const STATUS_COLORS = {
  inquiry: 'bg-blue-500',
  quoted: 'bg-yellow-500',
  scheduled: 'bg-purple-500',
  in_progress: 'bg-orange-500',
  completed: 'bg-green-500',
  invoiced: 'bg-cyan-500',
  paid: 'bg-emerald-500',
  cancelled: 'bg-red-500'
};

const STATUS_LABELS = {
  inquiry: 'Inquiry',
  quoted: 'Quoted',
  scheduled: 'Scheduled',
  in_progress: 'In Progress',
  completed: 'Completed',
  invoiced: 'Invoiced',
  paid: 'Paid',
  cancelled: 'Cancelled'
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [estimates, setEstimates] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedEstimate, setSelectedEstimate] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({});

  const getAuthHeader = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('adminToken')}` }
  });

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      navigate('/admin');
      return;
    }
    fetchData();
  }, [navigate, statusFilter]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [estimatesRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/estimates${statusFilter ? `?status=${statusFilter}` : ''}`, getAuthHeader()),
        axios.get(`${API}/admin/stats`, getAuthHeader())
      ]);
      setEstimates(estimatesRes.data);
      setStats(statsRes.data);
    } catch (err) {
      console.error('Error fetching data:', err);
      if (err.response?.status === 401) {
        localStorage.removeItem('adminToken');
        navigate('/admin');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    navigate('/admin');
  };

  const handleUpdateStatus = async (estimateId, newStatus) => {
    try {
      await axios.put(`${API}/admin/estimates/${estimateId}`, { status: newStatus }, getAuthHeader());
      fetchData();
      if (selectedEstimate?.id === estimateId) {
        setSelectedEstimate({ ...selectedEstimate, status: newStatus });
      }
    } catch (err) {
      console.error('Error updating status:', err);
    }
  };

  const handleUpdateEstimate = async () => {
    try {
      await axios.put(`${API}/admin/estimates/${selectedEstimate.id}`, editData, getAuthHeader());
      fetchData();
      setSelectedEstimate({ ...selectedEstimate, ...editData });
      setIsEditing(false);
    } catch (err) {
      console.error('Error updating estimate:', err);
    }
  };

  const handleDeleteEstimate = async (estimateId) => {
    if (!window.confirm('Are you sure you want to delete this estimate?')) return;
    try {
      await axios.delete(`${API}/admin/estimates/${estimateId}`, getAuthHeader());
      fetchData();
      setSelectedEstimate(null);
    } catch (err) {
      console.error('Error deleting estimate:', err);
    }
  };

  const filteredEstimates = estimates.filter(est => 
    est.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    est.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    est.phone.includes(searchTerm)
  );

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-[#0d1f33]">
      {/* Header */}
      <header className="bg-[#152a42] border-b border-[#F7931E]/20 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src={LOGO_URL} alt="Top Notch Tile LLC" className="h-12" />
            <span className="text-white font-bold text-xl hidden md:block">Admin Dashboard</span>
          </div>
          <div className="flex items-center gap-4">
            <button onClick={fetchData} className="p-2 text-[#8fa3b8] hover:text-white transition-colors">
              <RefreshCw size={20} className={loading ? 'animate-spin' : ''} />
            </button>
            <button onClick={handleLogout} className="flex items-center gap-2 text-[#8fa3b8] hover:text-white transition-colors">
              <LogOut size={20} />
              <span className="hidden md:inline">Logout</span>
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4 mb-6">
            <div className="bg-[#152a42] rounded-lg p-4 border border-[#F7931E]/20">
              <p className="text-[#8fa3b8] text-sm">Total</p>
              <p className="text-2xl font-bold text-white">{stats.total}</p>
            </div>
            {Object.entries(stats.by_status).map(([status, count]) => (
              <div key={status} className="bg-[#152a42] rounded-lg p-4 border border-[#F7931E]/20">
                <p className="text-[#8fa3b8] text-sm capitalize">{STATUS_LABELS[status]}</p>
                <p className="text-2xl font-bold text-white">{count}</p>
              </div>
            ))}
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8fa3b8]" />
            <input
              type="text"
              placeholder="Search by name, email, or phone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="form-input rounded-lg pl-10 w-full"
            />
          </div>
          <div className="relative">
            <Filter size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8fa3b8]" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="form-input rounded-lg pl-10 pr-10 appearance-none cursor-pointer min-w-[180px]"
            >
              <option value="">All Statuses</option>
              {Object.entries(STATUS_LABELS).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
            <ChevronDown size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8fa3b8] pointer-events-none" />
          </div>
        </div>

        {/* Estimates Table */}
        <div className="bg-[#152a42] rounded-lg border border-[#F7931E]/20 overflow-hidden">
          {loading ? (
            <div className="p-8 text-center">
              <RefreshCw size={32} className="animate-spin text-[#F7931E] mx-auto mb-2" />
              <p className="text-[#8fa3b8]">Loading estimates...</p>
            </div>
          ) : filteredEstimates.length === 0 ? (
            <div className="p-8 text-center">
              <FileText size={48} className="text-[#8fa3b8] mx-auto mb-2" />
              <p className="text-[#8fa3b8]">No estimates found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#0d1f33]">
                  <tr>
                    <th className="text-left p-4 text-[#8fa3b8] font-medium">Customer</th>
                    <th className="text-left p-4 text-[#8fa3b8] font-medium hidden md:table-cell">Project</th>
                    <th className="text-left p-4 text-[#8fa3b8] font-medium">Status</th>
                    <th className="text-left p-4 text-[#8fa3b8] font-medium hidden lg:table-cell">Date</th>
                    <th className="text-right p-4 text-[#8fa3b8] font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredEstimates.map((estimate) => (
                    <tr key={estimate.id} className="border-t border-[#F7931E]/10 hover:bg-[#0d1f33]/50">
                      <td className="p-4">
                        <p className="text-white font-medium">{estimate.full_name}</p>
                        <p className="text-[#8fa3b8] text-sm">{estimate.email}</p>
                        <p className="text-[#8fa3b8] text-sm">{estimate.phone}</p>
                      </td>
                      <td className="p-4 hidden md:table-cell">
                        <p className="text-[#c5d3e3]">{estimate.project_types?.join(', ') || 'N/A'}</p>
                        <p className="text-[#8fa3b8] text-sm">{estimate.project_size || 'Size not specified'}</p>
                      </td>
                      <td className="p-4">
                        <select
                          value={estimate.status}
                          onChange={(e) => handleUpdateStatus(estimate.id, e.target.value)}
                          className={`${STATUS_COLORS[estimate.status]} text-white text-sm px-3 py-1 rounded-full cursor-pointer border-0 appearance-none`}
                        >
                          {Object.entries(STATUS_LABELS).map(([value, label]) => (
                            <option key={value} value={value}>{label}</option>
                          ))}
                        </select>
                      </td>
                      <td className="p-4 hidden lg:table-cell">
                        <p className="text-[#8fa3b8] text-sm">{formatDate(estimate.created_at)}</p>
                      </td>
                      <td className="p-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => { setSelectedEstimate(estimate); setIsEditing(false); }}
                            className="p-2 text-[#8fa3b8] hover:text-[#F7931E] transition-colors"
                            title="View Details"
                          >
                            <Eye size={18} />
                          </button>
                          <button
                            onClick={() => handleDeleteEstimate(estimate.id)}
                            className="p-2 text-[#8fa3b8] hover:text-red-500 transition-colors"
                            title="Delete"
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Detail Modal */}
      {selectedEstimate && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-[#152a42] rounded-lg w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-[#152a42] border-b border-[#F7931E]/20 p-4 flex items-center justify-between">
              <h2 className="text-xl font-bold text-white">Estimate Details</h2>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => { setIsEditing(!isEditing); setEditData(selectedEstimate); }}
                  className="p-2 text-[#8fa3b8] hover:text-[#F7931E] transition-colors"
                >
                  <Edit2 size={18} />
                </button>
                <button
                  onClick={() => setSelectedEstimate(null)}
                  className="p-2 text-[#8fa3b8] hover:text-white transition-colors"
                >
                  <X size={18} />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Status & Quick Actions */}
              <div className="flex flex-wrap items-center gap-4">
                <select
                  value={selectedEstimate.status}
                  onChange={(e) => handleUpdateStatus(selectedEstimate.id, e.target.value)}
                  className={`${STATUS_COLORS[selectedEstimate.status]} text-white px-4 py-2 rounded-full cursor-pointer border-0`}
                >
                  {Object.entries(STATUS_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
                <span className="text-[#8fa3b8] text-sm">
                  Created: {formatDate(selectedEstimate.created_at)}
                </span>
              </div>

              {/* Contact Info */}
              <div className="bg-[#0d1f33] rounded-lg p-4">
                <h3 className="text-[#F7931E] font-semibold mb-3">Contact Information</h3>
                {isEditing ? (
                  <div className="grid md:grid-cols-2 gap-4">
                    <input
                      type="text"
                      value={editData.full_name || ''}
                      onChange={(e) => setEditData({ ...editData, full_name: e.target.value })}
                      className="form-input rounded-lg"
                      placeholder="Full Name"
                    />
                    <input
                      type="tel"
                      value={editData.phone || ''}
                      onChange={(e) => setEditData({ ...editData, phone: e.target.value })}
                      className="form-input rounded-lg"
                      placeholder="Phone"
                    />
                    <input
                      type="email"
                      value={editData.email || ''}
                      onChange={(e) => setEditData({ ...editData, email: e.target.value })}
                      className="form-input rounded-lg"
                      placeholder="Email"
                    />
                    <input
                      type="text"
                      value={editData.project_address || ''}
                      onChange={(e) => setEditData({ ...editData, project_address: e.target.value })}
                      className="form-input rounded-lg"
                      placeholder="Address"
                    />
                  </div>
                ) : (
                  <div className="grid md:grid-cols-2 gap-3">
                    <div className="flex items-center gap-2 text-white">
                      <span className="font-medium">{selectedEstimate.full_name}</span>
                    </div>
                    <div className="flex items-center gap-2 text-[#c5d3e3]">
                      <Phone size={16} className="text-[#F7931E]" />
                      <a href={`tel:${selectedEstimate.phone}`} className="hover:text-[#F7931E]">{selectedEstimate.phone}</a>
                    </div>
                    <div className="flex items-center gap-2 text-[#c5d3e3]">
                      <Mail size={16} className="text-[#F7931E]" />
                      <a href={`mailto:${selectedEstimate.email}`} className="hover:text-[#F7931E]">{selectedEstimate.email}</a>
                    </div>
                    {selectedEstimate.project_address && (
                      <div className="flex items-center gap-2 text-[#c5d3e3]">
                        <MapPin size={16} className="text-[#F7931E]" />
                        <span>{selectedEstimate.project_address}</span>
                      </div>
                    )}
                  </div>
                )}
                {selectedEstimate.preferred_contact?.length > 0 && (
                  <p className="text-[#8fa3b8] text-sm mt-2">
                    Preferred: {selectedEstimate.preferred_contact.join(', ')}
                  </p>
                )}
              </div>

              {/* Project Details */}
              <div className="bg-[#0d1f33] rounded-lg p-4">
                <h3 className="text-[#F7931E] font-semibold mb-3">Project Details</h3>
                <div className="space-y-2 text-[#c5d3e3]">
                  <p><span className="text-[#8fa3b8]">Type:</span> {selectedEstimate.project_types?.join(', ') || 'N/A'}</p>
                  {selectedEstimate.project_type_other && (
                    <p><span className="text-[#8fa3b8]">Other:</span> {selectedEstimate.project_type_other}</p>
                  )}
                  <p><span className="text-[#8fa3b8]">Size:</span> {selectedEstimate.project_size || 'N/A'}</p>
                  {selectedEstimate.known_square_footage && (
                    <p><span className="text-[#8fa3b8]">Square Footage:</span> {selectedEstimate.known_square_footage} sq ft</p>
                  )}
                  <p><span className="text-[#8fa3b8]">Timeline:</span> {selectedEstimate.timeline || 'N/A'}</p>
                  <p><span className="text-[#8fa3b8]">Space in Use:</span> {selectedEstimate.space_in_use || 'N/A'}</p>
                  <p><span className="text-[#8fa3b8]">Budget:</span> {selectedEstimate.budget || 'N/A'}</p>
                </div>
              </div>

              {/* Tile & Surface Info */}
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-[#0d1f33] rounded-lg p-4">
                  <h3 className="text-[#F7931E] font-semibold mb-3">Tile Information</h3>
                  <div className="space-y-2 text-[#c5d3e3]">
                    <p><span className="text-[#8fa3b8]">Selected:</span> {selectedEstimate.tile_selected || 'N/A'}</p>
                    <p><span className="text-[#8fa3b8]">Types:</span> {selectedEstimate.tile_types?.join(', ') || 'N/A'}</p>
                    <p><span className="text-[#8fa3b8]">Sizes:</span> {selectedEstimate.tile_sizes?.join(', ') || 'N/A'}</p>
                  </div>
                </div>
                <div className="bg-[#0d1f33] rounded-lg p-4">
                  <h3 className="text-[#F7931E] font-semibold mb-3">Surface & Prep</h3>
                  <div className="space-y-2 text-[#c5d3e3]">
                    <p><span className="text-[#8fa3b8]">Installation:</span> {selectedEstimate.installation_type || 'N/A'}</p>
                    <p><span className="text-[#8fa3b8]">Condition:</span> {selectedEstimate.surface_condition || 'N/A'}</p>
                    <p><span className="text-[#8fa3b8]">Issues:</span> {selectedEstimate.additional_conditions?.join(', ') || 'None'}</p>
                  </div>
                </div>
              </div>

              {/* Notes */}
              {selectedEstimate.additional_notes && (
                <div className="bg-[#0d1f33] rounded-lg p-4">
                  <h3 className="text-[#F7931E] font-semibold mb-3">Customer Notes</h3>
                  <p className="text-[#c5d3e3] whitespace-pre-wrap">{selectedEstimate.additional_notes}</p>
                </div>
              )}

              {/* Photos */}
              {selectedEstimate.photos && selectedEstimate.photos.length > 0 && (
                <div className="bg-[#0d1f33] rounded-lg p-4">
                  <h3 className="text-[#F7931E] font-semibold mb-3">Uploaded Photos ({selectedEstimate.photos.length})</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {selectedEstimate.photos.map((photo, index) => (
                      <a
                        key={index}
                        href={`${API}/photos/${photo.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block"
                      >
                        <img
                          src={`${API}/photos/${photo.id}`}
                          alt={photo.filename || `Photo ${index + 1}`}
                          className="w-full h-24 object-cover rounded-lg hover:opacity-80 transition-opacity"
                        />
                        <p className="text-xs text-[#8fa3b8] truncate mt-1">{photo.filename || `Photo ${index + 1}`}</p>
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {/* Admin Notes */}
              <div className="bg-[#0d1f33] rounded-lg p-4">
                <h3 className="text-[#F7931E] font-semibold mb-3">Admin Notes & Quote</h3>
                {isEditing ? (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-[#8fa3b8] text-sm mb-1">Quote Amount ($)</label>
                      <input
                        type="number"
                        value={editData.quote_amount || ''}
                        onChange={(e) => setEditData({ ...editData, quote_amount: parseFloat(e.target.value) || null })}
                        className="form-input rounded-lg w-full"
                        placeholder="Enter quote amount"
                      />
                    </div>
                    <div>
                      <label className="block text-[#8fa3b8] text-sm mb-1">Internal Notes</label>
                      <textarea
                        value={editData.admin_notes || ''}
                        onChange={(e) => setEditData({ ...editData, admin_notes: e.target.value })}
                        className="form-input rounded-lg w-full resize-none"
                        rows={3}
                        placeholder="Add internal notes..."
                      />
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {selectedEstimate.quote_amount && (
                      <p className="text-white text-lg font-bold flex items-center gap-2">
                        <DollarSign size={18} className="text-[#F7931E]" />
                        Quote: ${selectedEstimate.quote_amount.toLocaleString()}
                      </p>
                    )}
                    <p className="text-[#c5d3e3]">{selectedEstimate.admin_notes || 'No admin notes yet.'}</p>
                  </div>
                )}
              </div>

              {/* Edit Actions */}
              {isEditing && (
                <div className="flex justify-end gap-4">
                  <button
                    onClick={() => setIsEditing(false)}
                    className="btn-secondary"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleUpdateEstimate}
                    className="btn-primary"
                  >
                    <Check size={18} />
                    Save Changes
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
